// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the step-up box (decision O4, owner 2026-10-06: a passkey first,
 * an emailed code where the account has none; `functions/api/data/_stepup.ts`).
 * A risky action the site answers with `step_up_required` asks here; the
 * site says which: **Use my passkey** (the browser's own passkey prompt,
 * user verification required), or a six-digit code mailed to the account.
 * Once proved, the action is tried again. `guarded(slot, action, call)`
 * wraps any such call.
 */

import { t, type MessageKey } from '../i18n'
import { startStepUp, verifyStepUp, type ApiResult } from '../services/ownDataApi'
import { usePasskey } from '../services/passkeyClient'
import { isStepUpAction, isStrictStepUp, type StepUpAction } from '../services/ownDataSharing'
import { button, h } from './myDataDom'

const ACTION_TEXT: Record<StepUpAction, MessageKey> = {
  'source.add': 'myData.stepUp.action.source',
  'share.create': 'myData.stepUp.action.share',
  'meeting.share': 'myData.stepUp.action.meeting',
  'dataset.open': 'myData.stepUp.action.open',
  'dataset.delete': 'myData.stepUp.action.delete',
  'exact.export': 'myData.stepUp.action.export',
  'role.change': 'myData.stepUp.action.role',
  'passkey.add': 'myData.stepUp.action.passkeyAdd',
  'passkey.remove': 'myData.stepUp.action.passkeyRemove',
  'passkey.keep': 'myData.stepUp.action.passkeyKeep',
  'owner.handover': 'myData.stepUp.action.ownerHandover',
  'account.delete': 'myData.stepUp.action.accountDelete',
}

const ERROR_TEXT: Record<string, MessageKey> = {
  wrong_code: 'myData.stepUp.wrong',
  wrong_passkey: 'myData.stepUp.wrongPasskey',
  code_expired: 'myData.stepUp.expired',
  bad_code: 'myData.stepUp.badFormat',
  too_many_codes: 'myData.stepUp.tooMany',
  step_up_locked: 'myData.stepUp.locked',
  mail_unavailable: 'myData.stepUp.noMail',
  mail_failed: 'myData.stepUp.noMail',
  rate_limited: 'myData.error.rateLimited',
  NotAllowedError: 'myData.stepUp.passkeyCancelled',
  cancelled: 'myData.stepUp.passkeyCancelled',
  SecurityError: 'myData.stepUp.passkeyUnavailable',
  NotSupportedError: 'myData.stepUp.passkeyUnavailable',
}

/**
 * Ask for a step-up in `slot` and wait: true once the site accepted it,
 * false when the person cancelled.
 */
export function askStepUp(slot: HTMLElement, action: StepUpAction, opts: { lostPasskey?: boolean } = {}): Promise<boolean> {
  return new Promise((resolve) => {
    let challenge: string | null = null
    let method: 'passkey' | 'email' | null = null
    let publicKey: Parameters<typeof usePasskey>[0] | null = null
    const status = h('p', { class: 'mydata-hint', role: 'status' }, t('myData.stepUp.preparing'))
    const error = h('p', { class: 'mydata-notice is-error', role: 'alert', hidden: true })
    const input = h('input', { type: 'text', id: 'mydata-stepup-code', class: 'mydata-input mydata-stepup-code', inputmode: 'numeric', autocomplete: 'one-time-code', maxlength: 8, pattern: '[0-9]{8}', spellcheck: 'false' })
    const codeLabel = h('label', { class: 'mydata-label', for: 'mydata-stepup-code' }, t('myData.stepUp.codeLabel'))
    const confirm = button('pl-tab-btn mydata-stepup-confirm', t('myData.stepUp.confirm'), { disabled: true })
    const passkeyBtn = button('pl-tab-btn mydata-stepup-passkey', t('myData.stepUp.usePasskey'))
    const again = button('pl-tab-btn pl-tab-btn-quiet mydata-stepup-again', t('myData.stepUp.again'))
    const cancel = button('pl-tab-btn pl-tab-btn-quiet mydata-stepup-cancel', t('myData.stepUp.cancel'))
    const codePart = h('div', { class: 'mydata-stepup-code-part', hidden: true }, codeLabel, input)
    const box = h('section', { class: 'mydata-stepup', role: 'dialog', 'aria-modal': 'false', 'aria-labelledby': 'mydata-stepup-title' },
      h('h4', { id: 'mydata-stepup-title' }, t('myData.stepUp.title')),
      h('p', null, t(isStrictStepUp(action) ? 'myData.stepUp.whyOnce' : 'myData.stepUp.whyAny', { action: t(ACTION_TEXT[action]) })),
      status,
      codePart,
      error,
      h('div', { class: 'mydata-actions' }, passkeyBtn, confirm, again, cancel))
    passkeyBtn.hidden = true
    confirm.hidden = true
    slot.replaceChildren(box)

    const finish = (ok: boolean) => {
      slot.replaceChildren()
      resolve(ok)
    }
    const fail = (code: string) => {
      error.textContent = t(ERROR_TEXT[code] ?? 'myData.error.generic')
      error.hidden = false
    }
    const start = async (keepError = false) => {
      again.disabled = true
      if (!keepError) error.hidden = true
      status.textContent = t('myData.stepUp.preparing')
      const r = await startStepUp(action, opts.lostPasskey === true)
      again.disabled = false
      if (!r.ok) return fail(r.error)
      if (!r.value.required) return finish(true)
      challenge = r.value.challenge ?? null
      method = r.value.method === 'passkey' ? 'passkey' : 'email'
      publicKey = r.value.publicKey ?? null
      if (method === 'passkey') {
        status.textContent = t('myData.stepUp.passkeyAsk')
        passkeyBtn.hidden = false
        confirm.hidden = true
        codePart.hidden = true
        again.textContent = t('myData.stepUp.passkeyAgain')
        passkeyBtn.focus()
      } else {
        status.textContent = t('myData.stepUp.sent', { to: r.value.sentTo ?? '' })
        codePart.hidden = false
        confirm.hidden = false
        passkeyBtn.hidden = true
        input.focus()
      }
    }
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 8)
      confirm.disabled = input.value.length !== 8 || !challenge
    })
    const submitCode = async () => {
      if (!challenge || input.value.length !== 8) return
      confirm.disabled = true
      const r: ApiResult<{ verified: boolean }> = await verifyStepUp(challenge, { code: input.value })
      confirm.disabled = false
      if (r.ok && r.value.verified) return finish(true)
      fail(r.ok ? 'wrong_code' : r.error)
      if (!r.ok && r.error === 'code_expired') challenge = null
    }
    const submitPasskey = async () => {
      if (!challenge || !publicKey) return
      passkeyBtn.disabled = true
      const a = await usePasskey(publicKey)
      if (!a.ok) {
        passkeyBtn.disabled = false
        return fail(a.error)
      }
      const r = await verifyStepUp(challenge, { assertion: a.assertion })
      passkeyBtn.disabled = false
      if (r.ok && r.value.verified) return finish(true)
      // A passkey challenge is single use: get a new one for the next try.
      fail(r.ok ? 'wrong_passkey' : r.error)
      challenge = null
      void start(true)
    }
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault() // inside a form (Delete account): Enter answers the code, never submits the form
        void submitCode()
      }
      if (e.key === 'Escape') finish(false)
    })
    confirm.addEventListener('click', () => void submitCode())
    passkeyBtn.addEventListener('click', () => void submitPasskey())
    again.addEventListener('click', () => void start())
    cancel.addEventListener('click', () => finish(false))
    box.scrollIntoView?.({ block: 'nearest' })
    void start()
  })
}

/** Run a risky call; if the site asks for a step-up, ask for one and run it once more. */
export async function guarded<T>(slot: HTMLElement, action: StepUpAction, run: () => Promise<ApiResult<T>>): Promise<ApiResult<T>> {
  const first = await run()
  if (first.ok || first.error !== 'step_up_required') return first
  // The site names the action it wants proved (some need a step-up of their own, review I-3).
  const asked = first.body?.action
  if (!(await askStepUp(slot, isStepUpAction(asked) ? asked : action))) return { ok: false, status: 403, error: 'step_up_cancelled' }
  return run()
}
